import { useCallback, useEffect, useState } from 'react';
import {
  Briefcase,
  Building2,
  CalendarOff,
  ChevronLeft,
  ChevronRight,
  House,
  MapPin,
  Pencil,
  Repeat,
  Trash2,
} from 'lucide-react';
import Modal from '../components/Modal.jsx';
import { buildRRuleClient } from '../components/RecurrencePicker.jsx';
import { apiPersons, apiWorkLocations } from '../api/client.js';
import MobileUnavailabilityModal from './MobileUnavailabilityModal.jsx';
import {
  todayUtc,
  mondayOf,
  addDays,
  isoDate,
  isWeekday,
  parseIso,
  weekDays,
  fullDayLabel,
  weekRangeLabel,
} from './dateUtils.js';

/**
 * Sprint App native — « Mon statut » : lieu de travail par jour de la semaine
 * (ticket #6, contrat d'auth §7 bis). Mêmes appels et payloads que la
 * `StatusModal` du Schedule desktop :
 *  - GET  /work-locations?from&to   (demi-journées, bornées à soi en mobile) ;
 *  - POST /work-locations/set       { personId, date, location, half, recurrenceRule } ;
 *  - POST /work-locations/clear     { personId, date, half }.
 * Récurrence (2026-10-02) : la modale propose une répétition hebdomadaire,
 * bimensuelle ou du lundi au vendredi (RRULE via `buildRRuleClient`), et
 * l'arrêt d'une série à partir d'un jour (`clear` scope `this_and_future`).
 * Les pastilles « journée » posent toujours un statut d'un seul jour.
 * Les jours non ouvrés / fériés sont refusés par le serveur (message affiché).
 *
 * #106 — un sous-traitant y trouve aussi « Déclarer une indisponibilité »
 * (même modale que « Mon planning », où l'indisponibilité s'affiche et se
 * supprime) : c'est ici qu'il la cherche spontanément.
 */

// Couleurs = variables du thème (jamais de couleur en dur).
const LOCATIONS = [
  { value: 'office', label: 'Bureau', Icon: Building2, color: 'var(--accent)' },
  { value: 'client', label: 'Site client', Icon: Briefcase, color: 'var(--warning)' },
  { value: 'remote', label: 'Télétravail', Icon: House, color: 'var(--success)' },
  { value: 'other', label: 'Autre', Icon: MapPin, color: 'var(--text-muted)' },
];
const LOCATION_BY_VALUE = new Map(LOCATIONS.map((l) => [l.value, l]));

const HALVES = [
  { value: 'full', label: 'Journée' },
  { value: 'morning', label: 'Matin' },
  { value: 'afternoon', label: 'Après-midi' },
];

/** Regroupe les demi-journées d'une date : { morning, afternoon, full }. */
function dayStatus(slots) {
  const day = { morning: null, afternoon: null, full: false };
  for (const s of slots) day[s.slot] = s;
  day.full = Boolean(
    day.morning &&
      day.afternoon &&
      day.morning.isFull &&
      day.afternoon.isFull &&
      day.morning.entryId === day.afternoon.entryId,
  );
  return day;
}

function LocationChip({ slot, prefix }) {
  const loc = LOCATION_BY_VALUE.get(slot.location);
  const Icon = loc?.Icon ?? MapPin;
  return (
    <span className="m-status-chip" style={{ '--chip-color': loc?.color ?? 'var(--accent)' }}>
      <Icon size={14} />
      {prefix ? `${prefix} : ` : ''}
      {loc?.label ?? slot.location}
      {slot.isRecurring ? ' (récurrent)' : ''}
    </span>
  );
}

export default function MobileStatus({ user }) {
  const [weekStart, setWeekStart] = useState(() => mondayOf(todayUtc()));
  const [myPersonId, setMyPersonId] = useState(null);
  const [isContractor, setIsContractor] = useState(false);
  const [declaring, setDeclaring] = useState(false);
  const [personResolved, setPersonResolved] = useState(false);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyIso, setBusyIso] = useState(null);
  const [editing, setEditing] = useState(null); // { iso, date, status }
  const [toast, setToast] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await apiPersons.me();
        if (!cancelled) {
          setMyPersonId(me?.id ?? null);
          setIsContractor(me?.employeeType === 'CONTRACTOR');
        }
      } catch {
        if (!cancelled) setMyPersonId(null);
      } finally {
        if (!cancelled) setPersonResolved(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const showToast = useCallback((kind, message) => {
    setToast({ kind, message });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const fetchWeek = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const data = await apiWorkLocations.list({
          from: isoDate(weekStart),
          to: isoDate(addDays(weekStart, 6)),
        });
        setSlots(data?.items ?? []);
        setError(null);
      } catch (e) {
        setError(e?.code === 'mobile_scope' ? e.message : 'load');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [weekStart],
  );

  useEffect(() => {
    if (!personResolved || !myPersonId) {
      if (personResolved) setLoading(false);
      return;
    }
    fetchWeek(false);
  }, [personResolved, myPersonId, fetchWeek]);

  // Pose un statut « journée » en un geste (pill du jour).
  const setFullDay = async (iso, location) => {
    if (!myPersonId || busyIso) return;
    setBusyIso(iso);
    try {
      await apiWorkLocations.set({
        personId: myPersonId,
        date: iso,
        location,
        half: 'full',
        recurrenceRule: null,
      });
      await fetchWeek(true);
    } catch (e) {
      showToast('error', e?.message || 'Enregistrement impossible.');
    } finally {
      setBusyIso(null);
    }
  };

  // Efface le statut de la journée entière (matin et après-midi) en un geste ;
  // la modale reste là pour n'effacer qu'une demi-journée.
  const clearDay = async (iso) => {
    if (!myPersonId || busyIso) return;
    setBusyIso(iso);
    try {
      await apiWorkLocations.clear({ personId: myPersonId, date: iso, half: 'full' });
      await fetchWeek(true);
      showToast('ok', 'Statut supprimé.');
    } catch (e) {
      showToast('error', e?.message || 'Suppression impossible.');
    } finally {
      setBusyIso(null);
    }
  };

  const mine = slots.filter((s) => s.personId === myPersonId);
  const todayIso = isoDate(todayUtc());
  // Lun→Ven, plus un week-end seulement s'il porte déjà un statut.
  const days = weekDays(weekStart).filter(
    (d) => isWeekday(d) || mine.some((s) => s.date === isoDate(d)),
  );

  return (
    <div>
      <div className="m-weeknav">
        <button
          type="button"
          className="m-navbtn"
          aria-label="Semaine précédente"
          onClick={() => setWeekStart((w) => addDays(w, -7))}
        >
          <ChevronLeft size={18} />
        </button>
        <span className="m-weeknav__title">{weekRangeLabel(weekStart)}</span>
        <button
          type="button"
          className="m-navbtn"
          aria-label="Semaine suivante"
          onClick={() => setWeekStart((w) => addDays(w, 7))}
        >
          <ChevronRight size={18} />
        </button>
      </div>
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <button
          type="button"
          className="m-todaybtn"
          onClick={() => setWeekStart(mondayOf(todayUtc()))}
        >
          Cette semaine
        </button>
      </div>

      {isContractor && myPersonId && user?.role !== 'VIEWER' ? (
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <button type="button" className="m-btn" onClick={() => setDeclaring(true)}>
            <CalendarOff size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Déclarer une indisponibilité
          </button>
        </div>
      ) : null}

      {!personResolved || loading ? (
        <div className="m-state">Chargement…</div>
      ) : !myPersonId ? (
        <div className="m-state">Aucune fiche collaborateur liée à votre compte.</div>
      ) : error ? (
        <div className="m-state">
          {error === 'load' ? 'Impossible de charger vos statuts.' : error}
        </div>
      ) : (
        days.map((d) => {
          const iso = isoDate(d);
          const status = dayStatus(mine.filter((s) => s.date === iso));
          const fullLocation = status.full ? status.morning.location : null;
          const hasStatus = Boolean(status.morning || status.afternoon);
          // Jour issu d'une série : la corbeille ouvre la modale, qui demande
          // s'il faut effacer ce jour seulement ou arrêter la répétition.
          const isRecurring = Boolean(status.morning?.isRecurring || status.afternoon?.isRecurring);
          return (
            <div className="m-day" key={iso} data-busy={busyIso === iso}>
              <div className="m-day__head" data-today={iso === todayIso}>
                <span>{fullDayLabel(d)}</span>
                {hasStatus ? (
                  <button
                    type="button"
                    className="m-daybtn m-daybtn--danger"
                    style={{ marginLeft: 'auto' }}
                    disabled={busyIso === iso}
                    onClick={() =>
                      isRecurring ? setEditing({ iso, date: d, status }) : clearDay(iso)
                    }
                    title="Supprimer le statut du jour"
                    aria-label={`Supprimer le statut du ${fullDayLabel(d)}`}
                  >
                    <Trash2 size={15} />
                  </button>
                ) : null}
                <button
                  type="button"
                  className="m-daybtn"
                  style={hasStatus ? undefined : { marginLeft: 'auto' }}
                  onClick={() => setEditing({ iso, date: d, status })}
                  title="Modifier (demi-journée, effacer)"
                  aria-label={`Modifier le statut du ${fullDayLabel(d)}`}
                >
                  <Pencil size={15} />
                </button>
              </div>
              <div className="m-day__body">
                <div className="m-status-current">
                  {!hasStatus ? (
                    <span className="m-block__sub">Non renseigné</span>
                  ) : status.full ? (
                    <LocationChip slot={status.morning} />
                  ) : (
                    <>
                      {status.morning ? <LocationChip slot={status.morning} prefix="Matin" /> : null}
                      {status.afternoon ? (
                        <LocationChip slot={status.afternoon} prefix="Après-midi" />
                      ) : null}
                    </>
                  )}
                </div>
                <div className="m-pills">
                  {LOCATIONS.map(({ value, label, Icon, color }) => (
                    <button
                      key={value}
                      type="button"
                      className="m-pill"
                      style={{ '--pill-color': color }}
                      data-active={fullLocation === value}
                      disabled={busyIso === iso}
                      onClick={() => setFullDay(iso, value)}
                      title={`Journée : ${label}`}
                    >
                      <Icon size={14} />
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          );
        })
      )}

      <StatusModal
        ctx={editing}
        personId={myPersonId}
        onClose={() => setEditing(null)}
        onSaved={(message) => {
          setEditing(null);
          showToast('ok', message);
          fetchWeek(true);
        }}
      />

      {isContractor ? (
        <MobileUnavailabilityModal
          open={declaring}
          mode="create"
          defaultDate={(() => {
            const first = days.find((d) => isoDate(d) >= todayIso);
            return first ? isoDate(first) : todayIso;
          })()}
          personId={myPersonId}
          onClose={() => setDeclaring(false)}
          onSaved={(message) => {
            setDeclaring(false);
            showToast('ok', `${message} Elle apparaît dans « Mon planning ».`);
          }}
        />
      ) : null}

      {toast ? (
        <div className="m-toast" data-kind={toast.kind === 'error' ? 'error' : 'ok'}>
          {toast.message}
        </div>
      ) : null}
    </div>
  );
}

/** Répétition proposée en V1 mobile (sous-ensemble du `RecurrencePicker` web). */
const REPEATS = [
  { value: 'none', label: 'Non' },
  { value: 'weekly', label: 'Chaque semaine' },
  { value: 'biweekly', label: 'Toutes les 2 sem.' },
  { value: 'weekdays', label: 'Lun → ven' },
];
const WEEKDAYS = [
  { code: 'MO', label: 'Lun' },
  { code: 'TU', label: 'Mar' },
  { code: 'WE', label: 'Mer' },
  { code: 'TH', label: 'Jeu' },
  { code: 'FR', label: 'Ven' },
];
const RRULE_DAY_BY_UTC_DAY = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

/** Jour RRULE du jour ouvert (lundi par défaut un week-end). */
function weekdayCodeOf(iso) {
  const code = RRULE_DAY_BY_UTC_DAY[parseIso(iso).getUTCDay()];
  return WEEKDAYS.some((d) => d.code === code) ? code : 'MO';
}

/**
 * Modale « Statut de présence » : lieu, période, répétition, effacement.
 *
 * Répétition : la série démarre au jour ouvert (DTSTART = ce jour). Un jour
 * déjà couvert par une série reste modifiable seul (« Non » : le statut d'un
 * jour l'emporte sur la série) ; choisir une nouvelle répétition arrête
 * d'abord l'ancienne série à partir de ce jour (`clear` this_and_future),
 * pour ne pas laisser deux séries concurrentes sur les mêmes jours.
 */
function StatusModal({ ctx, personId, onClose, onSaved }) {
  const [location, setLocation] = useState('office');
  const [half, setHalf] = useState('full');
  const [repeat, setRepeat] = useState('none');
  const [days, setDays] = useState([]);
  const [until, setUntil] = useState('');
  const [clearScope, setClearScope] = useState('this_occurrence');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ctx) return;
    const s = ctx.status;
    const current = s.full ? s.morning : s.morning ?? s.afternoon;
    setLocation(current?.location ?? 'office');
    // Ticket #26 : période « Journée » par défaut, demi-journée si le statut
    // existant n'en couvre qu'une.
    setHalf(s.full || !current ? 'full' : s.morning ? 'morning' : 'afternoon');
    setRepeat('none');
    setDays([weekdayCodeOf(ctx.iso)]);
    setUntil('');
    setClearScope('this_occurrence');
    setSubmitting(false);
    setError(null);
  }, [ctx]);

  if (!ctx) return null;

  const s = ctx.status;
  const slotsForHalf = half === 'full' ? [s.morning, s.afternoon] : [s[half]];
  const existingForHalf = slotsForHalf.some(Boolean);
  const recurringForHalf = slotsForHalf.some((slot) => slot?.isRecurring);
  const isWeekly = repeat === 'weekly' || repeat === 'biweekly';

  const toggleDay = (code) =>
    setDays((prev) => (prev.includes(code) ? prev.filter((d) => d !== code) : [...prev, code]));

  const save = async () => {
    if (isWeekly && days.length === 0) {
      setError('Choisissez au moins un jour.');
      return;
    }
    if (until && until < ctx.iso) {
      setError('La date de fin précède le jour choisi.');
      return;
    }
    const recurrenceRule = buildRRuleClient({
      freq: repeat,
      byweekday: isWeekly ? days : [],
      endType: until ? 'until' : 'never',
      until: until || undefined,
    });
    setSubmitting(true);
    setError(null);
    try {
      if (recurrenceRule && recurringForHalf) {
        await apiWorkLocations.clear({
          personId,
          date: ctx.iso,
          half,
          scope: 'this_and_future',
        });
      }
      await apiWorkLocations.set({
        personId,
        date: ctx.iso,
        location,
        half,
        recurrenceRule,
      });
      onSaved(recurrenceRule ? 'Statut récurrent enregistré.' : 'Statut enregistré.');
    } catch (e) {
      setError(e?.message || 'Enregistrement impossible.');
      setSubmitting(false);
    }
  };

  const clear = async () => {
    setSubmitting(true);
    setError(null);
    const scope = recurringForHalf ? clearScope : 'this_occurrence';
    try {
      await apiWorkLocations.clear({ personId, date: ctx.iso, half, scope });
      onSaved(scope === 'this_and_future' ? 'Répétition arrêtée.' : 'Statut effacé.');
    } catch (e) {
      setError(e?.message || 'Effacement impossible.');
      setSubmitting(false);
    }
  };

  const close = submitting ? () => {} : onClose;

  return (
    <Modal
      open={Boolean(ctx)}
      onClose={close}
      title="Statut de présence"
      subtitle={fullDayLabel(ctx.date)}
      size="sm"
      footer={
        <div className="m-modal-footer">
          {existingForHalf ? (
            <button
              type="button"
              className="m-btn m-btn--danger-ghost"
              onClick={clear}
              disabled={submitting}
            >
              <Trash2 size={15} />
              {recurringForHalf && clearScope === 'this_and_future' ? 'Arrêter' : 'Effacer'}
            </button>
          ) : null}
          <span className="m-modal-footer__spacer" />
          <button type="button" className="m-btn" onClick={close} disabled={submitting}>
            Annuler
          </button>
          <button
            type="button"
            className="m-btn m-btn--primary"
            onClick={save}
            disabled={submitting}
          >
            {submitting ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      }
    >
      <div className="m-form">
        {recurringForHalf ? (
          <div className="m-notice" style={{ marginBottom: 0 }}>
            <Repeat size={14} style={{ flexShrink: 0 }} />
            <span>
              Statut récurrent. Sans répétition, l’enregistrement ne change que ce jour ;
              avec une répétition, il remplace la série à partir de ce jour.
            </span>
          </div>
        ) : null}

        <div className="m-field">
          <span className="m-field__label">Lieu</span>
          <div className="m-pills">
            {LOCATIONS.map(({ value, label, Icon, color }) => (
              <button
                key={value}
                type="button"
                className="m-pill"
                style={{ '--pill-color': color }}
                data-active={location === value}
                onClick={() => setLocation(value)}
              >
                <Icon size={14} />
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="m-field">
          <span className="m-field__label">Période</span>
          <div className="m-pills">
            {HALVES.map((h) => (
              <button
                key={h.value}
                type="button"
                className="m-pill"
                data-active={half === h.value}
                onClick={() => setHalf(h.value)}
              >
                {h.label}
              </button>
            ))}
          </div>
        </div>

        <div className="m-field">
          <span className="m-field__label">Répéter</span>
          <div className="m-pills">
            {REPEATS.map((r) => (
              <button
                key={r.value}
                type="button"
                className="m-pill"
                data-active={repeat === r.value}
                onClick={() => setRepeat(r.value)}
              >
                {r.value === 'none' ? null : <Repeat size={14} />}
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {isWeekly ? (
          <div className="m-field">
            <span className="m-field__label">Jours</span>
            <div className="m-pills">
              {WEEKDAYS.map((d) => (
                <button
                  key={d.code}
                  type="button"
                  className="m-pill"
                  data-active={days.includes(d.code)}
                  aria-pressed={days.includes(d.code)}
                  onClick={() => toggleDay(d.code)}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {repeat !== 'none' ? (
          <div className="m-field">
            <span className="m-field__label">Jusqu’au</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                className="m-input"
                type="date"
                value={until}
                min={ctx.iso}
                aria-label="Date de fin de la répétition"
                // ≥ 16px : sous ce seuil, iOS zoome au focus et ne dézoome pas.
                style={{ flex: 1, fontSize: 16 }}
                onChange={(e) => setUntil(e.target.value)}
              />
              {until ? (
                <button type="button" className="m-btn" onClick={() => setUntil('')}>
                  Sans fin
                </button>
              ) : null}
            </div>
            <span className="m-field__hint">
              {until
                ? 'Les jours non travaillés et fériés sont ignorés.'
                : 'Sans date : la répétition continue jusqu’à ce que vous l’arrêtiez.'}
            </span>
          </div>
        ) : null}

        {existingForHalf && recurringForHalf ? (
          <div className="m-field">
            <span className="m-field__label">Effacer</span>
            <div className="m-pills">
              <button
                type="button"
                className="m-pill"
                data-active={clearScope === 'this_occurrence'}
                onClick={() => setClearScope('this_occurrence')}
              >
                Ce jour seulement
              </button>
              <button
                type="button"
                className="m-pill"
                data-active={clearScope === 'this_and_future'}
                onClick={() => setClearScope('this_and_future')}
              >
                Ce jour et les suivants
              </button>
            </div>
          </div>
        ) : null}

        {error ? (
          <div className="m-field__error" role="alert">
            {error}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
